import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, ArrowLeft, RefreshCw, Eye, Shield, Trash2, X } from 'lucide-react';

const AdminImport = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [sheetsInfo, setSheetsInfo] = useState([]);
  const [knownSuppliers, setKnownSuppliers] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [confirmedSupplier, setConfirmedSupplier] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [updateStrategy, setUpdateStrategy] = useState('update_modified');
  const [protectImages, setProtectImages] = useState(true);
  const [collectionMode, setCollectionMode] = useState('add'); // 'add' ou 'replace'
  const [duplicateActions, setDuplicateActions] = useState({});
  const [collectionChangeActions, setCollectionChangeActions] = useState({}); // Actions pour changements de collection
  const [showDetailedDiff, setShowDetailedDiff] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [step, setStep] = useState(1); // 1: Upload, 2: Select Sheet + Confirm Supplier, 3: Analysis, 3.5: Image Validation, 4: Results
  
  // États pour la validation des images
  const [imageDetections, setImageDetections] = useState([]);
  const [imageStats, setImageStats] = useState(null);
  const [validatedImages, setValidatedImages] = useState({});
  
  // États pour le nettoyage
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [suppliersList, setSuppliersList] = useState([]);
  const [selectedSupplierCleanup, setSelectedSupplierCleanup] = useState('');
  const [selectedGammeCleanup, setSelectedGammeCleanup] = useState('');
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupStep, setCleanupStep] = useState(1); // 1: Select, 2: Preview, 3: Result

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Étape 1: Upload du fichier
  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    
    if (!uploadedFile) return;

    setFile(uploadedFile);
    
    const formData = new FormData();
    formData.append('file', uploadedFile);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/import/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Erreur lors de l\'upload');

      const data = await response.json();
      setFileId(data.file_id);
      setSheetsInfo(data.sheets);
      setKnownSuppliers(data.known_suppliers || []);
      setStep(2);
    } catch (error) {
      alert('Erreur lors de l\'upload du fichier: ' + error.message);
    }
  };

  // Étape 2: Analyser la feuille sélectionnée
  const handleAnalyzeSheet = async () => {
    if (!selectedSheet || !confirmedSupplier) {
      alert('Veuillez sélectionner une feuille et confirmer le fournisseur');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/import/analyze/${fileId}?sheet_name=${encodeURIComponent(selectedSheet.sheet_name)}&supplier=${encodeURIComponent(confirmedSupplier)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Erreur lors de l\'analyse');

      const data = await response.json();
      setAnalysis(data);
      
      // Initialiser les actions par défaut pour les produits modifiés
      const defaultActions = {};
      (data.modified_products || []).forEach(prod => {
        defaultActions[prod.ref_frs] = updateStrategy;
      });
      setDuplicateActions(defaultActions);
      
      // Détecter automatiquement les images
      const imagesFound = await detectImagesForImport(confirmedSupplier);
      
      // Si des images ont été trouvées, aller à l'étape de validation
      // Sinon, aller directement à l'étape d'analyse/import
      if (imagesFound) {
        setStep(3.5);
      } else {
        setStep(3);
      }
    } catch (error) {
      alert('Erreur lors de l\'analyse: ' + error.message);
    }
  };

  // Étape 3: Exécuter l'import
  const handleExecuteImport = async () => {
    setImporting(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin/import/execute`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            file_id: fileId,
            sheet_name: analysis.sheet_name,
            supplier: confirmedSupplier,
            duplicate_actions: duplicateActions,
            update_strategy: updateStrategy,
            protect_images: protectImages,
            collection_mode: collectionMode,
            collection_change_actions: collectionChangeActions
          })
        }
      );

      if (!response.ok) throw new Error('Erreur lors de l\'import');

      const data = await response.json();
      setImportResult(data);
      setStep(4);
    } catch (error) {
      alert('Erreur lors de l\'import: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setFileId(null);
    setSheetsInfo([]);
    setSelectedSheet(null);
    setConfirmedSupplier('');
    setAnalysis(null);
    setDuplicateActions({});
    setImportResult(null);
    setImageDetections([]);
    setImageStats(null);
    setValidatedImages({});
    setStep(1);
  };

  // Fonction pour détecter les images après l'analyse
  const detectImagesForImport = async (supplier) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/images/detect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ supplier })
      });

      if (!response.ok) {
        console.error('Détection images: réponse non-ok');
        return false;
      }

      const data = await response.json();
      setImageDetections(data.results || []);
      setImageStats(data.stats || {});
      
      // Auto-valider les détections uniques
      const autoValidated = {};
      (data.results || []).forEach(detection => {
        if (detection.status === 'found' && detection.images_found.length === 1) {
          autoValidated[detection.product_id] = detection.recommended_image;
        }
      });
      setValidatedImages(autoValidated);
      
      // Retourner true si des images ont été trouvées
      return data.stats && data.stats.found > 0;
    } catch (error) {
      console.error('Erreur détection images:', error);
      return false;
    }
  };

  const skipImageValidation = () => {
    setStep(3); // Aller directement à l'étape d'analyse
  };

  const applyImageAssociations = async () => {
    if (Object.keys(validatedImages).length === 0) {
      setStep(3);
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const associations = Object.entries(validatedImages).map(([productId, imageInfo]) => ({
        product_id: productId,
        image: imageInfo.image,
        thumbnail: imageInfo.thumbnail
      }));

      await fetch(`${BACKEND_URL}/api/admin/images/associate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ associations })
      });

      setStep(3); // Aller à l'étape suivante
    } catch (error) {
      console.error('Erreur application images:', error);
      alert('Erreur lors de l\'association des images');
    }
  };

  // Fonctions de nettoyage
  const openCleanupModal = async () => {
    setShowCleanupModal(true);
    setCleanupStep(1);
    
    // Charger la liste des fournisseurs
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/import/suppliers-list`);
      const data = await response.json();
      setSuppliersList(data.suppliers);
    } catch (error) {
      alert('Erreur lors du chargement des fournisseurs: ' + error.message);
    }
  };

  const previewCleanup = async () => {
    if (!selectedSupplierCleanup) {
      alert('Veuillez sélectionner un fournisseur');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/import/cleanup-products/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier: selectedSupplierCleanup,
          gamme: selectedGammeCleanup || null
        })
      });

      const data = await response.json();
      setCleanupPreview(data);
      setCleanupStep(2);
    } catch (error) {
      alert('Erreur lors de la prévisualisation: ' + error.message);
    }
  };

  const executeCleanup = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/import/cleanup-products/execute`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier: selectedSupplierCleanup,
          gamme: selectedGammeCleanup || null,
          preserve_images: true
        })
      });

      const data = await response.json();
      setCleanupResult(data);
      setCleanupStep(3);
    } catch (error) {
      alert('Erreur lors de la suppression: ' + error.message);
    }
  };

  const closeCleanupModal = () => {
    setShowCleanupModal(false);
    setSelectedSupplierCleanup('');
    setSelectedGammeCleanup('');
    setCleanupPreview(null);
    setCleanupResult(null);
    setCleanupStep(1);
    setCleanupStep(1);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-3xl font-bold">Import Excel - Toiles</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={openCleanupModal}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              <Trash2 size={16} />
              Nettoyage Produits
            </button>
            {step > 1 && step < 4 && (
              <button
                onClick={resetImport}
                className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                <RefreshCw size={16} />
                Recommencer
              </button>
            )}
          </div>
        </div>

        {/* Step Indicator */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-between">
            {['Upload', 'Sélection', 'Analyse', 'Résultats'].map((label, index) => (
              <div key={index} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  step > index + 1 ? 'bg-green-500 text-white' :
                  step === index + 1 ? 'bg-blue-500 text-white' :
                  'bg-gray-300 text-gray-600'
                }`}>
                  {step > index + 1 ? <CheckCircle size={20} /> : index + 1}
                </div>
                <span className="ml-2 text-sm font-medium">{label}</span>
                {index < 3 && <div className="w-16 h-1 bg-gray-300 mx-4" />}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="bg-white rounded-lg shadow p-8">
            <h2 className="text-2xl font-bold mb-4">📤 Upload Fichier Excel</h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <Upload className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-lg mb-4">Glissez-déposez votre fichier Excel ici ou cliquez pour sélectionner</p>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="inline-block px-6 py-3 bg-blue-500 text-white rounded cursor-pointer hover:bg-blue-600"
              >
                Sélectionner un fichier
              </label>
              <p className="text-sm text-gray-500 mt-4">Formats acceptés: .xlsx, .xls</p>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 rounded border border-blue-200">
              <h3 className="font-semibold mb-2">📋 Instructions:</h3>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Chaque feuille Excel = un fournisseur (DICKSON, S&P, etc.)</li>
                <li>La colonne <strong>ref-frs</strong> est obligatoire</li>
                <li>Le système détectera automatiquement les fournisseurs</li>
                <li>Les images existantes seront protégées</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Select Sheet & Confirm Supplier */}
        {step === 2 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">📊 Sélection de la Feuille</h2>
            <p className="text-gray-600 mb-4">Fichier: <strong>{file?.name}</strong></p>
            
            <div className="space-y-4">
              <h3 className="font-semibold">Feuilles détectées ({sheetsInfo.length}):</h3>
              
              {sheetsInfo.map((sheet, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedSheet(sheet);
                    setConfirmedSupplier(sheet.detected_supplier || '');
                  }}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedSheet?.sheet_name === sheet.sheet_name
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet size={24} className="text-green-600" />
                      <div>
                        <p className="font-semibold">{sheet.sheet_name}</p>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-green-700">{sheet.visible_row_count} lignes visibles</span>
                          {sheet.hidden_row_count > 0 && (
                            <span className="text-gray-500">
                              ({sheet.hidden_row_count} masquée{sheet.hidden_row_count > 1 ? 's' : ''})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {sheet.detected_supplier ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle size={20} className="text-green-500" />
                          <span className="text-sm">
                            Fournisseur: <strong>{sheet.detected_supplier}</strong>
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <AlertCircle size={20} className="text-orange-500" />
                          <span className="text-sm text-orange-600">Fournisseur à définir</span>
                        </div>
                      )}
                      
                      {selectedSheet?.sheet_name === sheet.sheet_name && (
                        <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                  
                  {selectedSheet?.sheet_name === sheet.sheet_name && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label className="block text-sm font-medium mb-2">
                        Confirmer ou modifier le fournisseur:
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={confirmedSupplier}
                          onChange={(e) => setConfirmedSupplier(e.target.value)}
                          placeholder="Nom du fournisseur"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded"
                          list="known-suppliers"
                        />
                        <datalist id="known-suppliers">
                          {knownSuppliers.map((s, i) => (
                            <option key={i} value={s} />
                          ))}
                        </datalist>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleAnalyzeSheet}
                disabled={!selectedSheet || !confirmedSupplier}
                className="px-6 py-3 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Analyser la feuille
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Analysis & Strategy */}
        {step === 3 && analysis && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold mb-4">📊 Analyse du Fichier</h2>
              <p className="text-gray-600 mb-4">
                Fournisseur: <strong>{confirmedSupplier}</strong> | Feuille: <strong>{analysis.sheet_name}</strong>
              </p>
              
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-green-50 border border-green-200 rounded p-4 text-center">
                  <CheckCircle className="mx-auto mb-2 text-green-600" size={32} />
                  <p className="text-2xl font-bold text-green-700">{analysis.analysis.identical_count}</p>
                  <p className="text-sm text-gray-600">Identiques</p>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded p-4 text-center">
                  <RefreshCw className="mx-auto mb-2 text-orange-600" size={32} />
                  <p className="text-2xl font-bold text-orange-700">{analysis.analysis.modified_count}</p>
                  <p className="text-sm text-gray-600">Modifiés</p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded p-4 text-center">
                  <FileSpreadsheet className="mx-auto mb-2 text-blue-600" size={32} />
                  <p className="text-2xl font-bold text-blue-700">{analysis.analysis.new_count}</p>
                  <p className="text-sm text-gray-600">Nouveaux</p>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded p-4 text-center">
                  <XCircle className="mx-auto mb-2 text-red-600" size={32} />
                  <p className="text-2xl font-bold text-red-700">{analysis.analysis.invalid_count}</p>
                  <p className="text-sm text-gray-600">Invalides</p>
                </div>
              </div>
            </div>

            {/* Update Strategy */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold mb-4">⚙️ Stratégie de Mise à Jour</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block font-medium mb-2">Mode de mise à jour:</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="strategy"
                        value="update_modified"
                        checked={updateStrategy === 'update_modified'}
                        onChange={(e) => setUpdateStrategy(e.target.value)}
                      />
                      <div>
                        <p className="font-medium">Mettre à jour modifié uniquement ⭐</p>
                        <p className="text-sm text-gray-600">Recommandé - Met à jour seulement les champs qui ont changé</p>
                      </div>
                    </label>
                    
                    <label className="flex items-center gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="strategy"
                        value="replace"
                        checked={updateStrategy === 'replace'}
                        onChange={(e) => setUpdateStrategy(e.target.value)}
                      />
                      <div>
                        <p className="font-medium">Tout remplacer</p>
                        <p className="text-sm text-gray-600">Supprime et recrée les produits existants</p>
                      </div>
                    </label>
                    
                    <label className="flex items-center gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="strategy"
                        value="skip"
                        checked={updateStrategy === 'skip'}
                        onChange={(e) => setUpdateStrategy(e.target.value)}
                      />
                      <div>
                        <p className="font-medium">Ignorer les doublons</p>
                        <p className="text-sm text-gray-600">N'importe que les nouveaux produits</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <input
                    type="checkbox"
                    id="protect-images"
                    checked={protectImages}
                    onChange={(e) => setProtectImages(e.target.checked)}
                    className="w-5 h-5"
                  />
                  <label htmlFor="protect-images" className="flex items-center gap-2 cursor-pointer">
                    <Shield size={20} className="text-blue-600" />
                    <div>
                      <p className="font-medium">Protéger les images existantes</p>
                      <p className="text-sm text-gray-600">Les images uploadées manuellement ne seront pas écrasées</p>
                    </div>
                  </label>
                </div>

                <div className="border border-gray-200 rounded p-3">
                  <label className="block font-medium mb-2">📋 Gérer les collections:</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="collectionMode"
                        value="add"
                        checked={collectionMode === 'add'}
                        onChange={(e) => setCollectionMode(e.target.value)}
                      />
                      <div>
                        <p className="font-medium">Ajouter aux collections existantes ⭐</p>
                        <p className="text-sm text-gray-600">Conserve les collections et ajoute la nouvelle (multi-collections)</p>
                      </div>
                    </label>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="collectionMode"
                        value="replace"
                        checked={collectionMode === 'replace'}
                        onChange={(e) => setCollectionMode(e.target.value)}
                      />
                      <div>
                        <p className="font-medium">Remplacer les collections</p>
                        <p className="text-sm text-gray-600">Écrase toutes les collections existantes avec la nouvelle</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Collection Changes - NOUVEAU */}
            {analysis.analysis.collection_change_count > 0 && (
              <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
                <h3 className="text-xl font-bold mb-4">⚠️ Changements de Collection ({analysis.analysis.collection_change_count} produits)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Ces produits existent déjà mais avec une collection différente. Choisissez l'action pour chaque produit :
                </p>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {analysis.collection_change_products.map((prod, index) => (
                    <div key={index} className="border-2 border-orange-300 rounded p-4 bg-orange-50">
                      <div className="mb-3">
                        <p className="font-semibold text-lg">{prod.ref_frs}</p>
                        <p className="text-sm text-gray-600">{prod.existing_name}</p>
                      </div>
                      
                      <div className="mb-3 p-3 bg-white rounded">
                        <p className="text-sm mb-1">
                          <span className="font-medium">Collection actuelle:</span> 
                          <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{prod.existing_gamme || 'Aucune'}</span>
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">Nouvelle collection:</span> 
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">{prod.new_gamme}</span>
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <label className="flex-1 flex items-center gap-2 p-3 border-2 rounded cursor-pointer hover:bg-white transition-colors"
                          style={{
                            borderColor: collectionChangeActions[prod.ref_frs] === 'add' || !collectionChangeActions[prod.ref_frs] ? '#10b981' : '#d1d5db',
                            backgroundColor: collectionChangeActions[prod.ref_frs] === 'add' || !collectionChangeActions[prod.ref_frs] ? '#ecfdf5' : 'white'
                          }}>
                          <input
                            type="radio"
                            name={`collection-change-${prod.ref_frs}`}
                            value="add"
                            checked={collectionChangeActions[prod.ref_frs] === 'add' || !collectionChangeActions[prod.ref_frs]}
                            onChange={() => setCollectionChangeActions({...collectionChangeActions, [prod.ref_frs]: 'add'})}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm">✅ Ajouter la nouvelle</p>
                            <p className="text-xs text-gray-600">Garder les deux collections</p>
                          </div>
                        </label>
                        
                        <label className="flex-1 flex items-center gap-2 p-3 border-2 rounded cursor-pointer hover:bg-white transition-colors"
                          style={{
                            borderColor: collectionChangeActions[prod.ref_frs] === 'replace' ? '#ef4444' : '#d1d5db',
                            backgroundColor: collectionChangeActions[prod.ref_frs] === 'replace' ? '#fef2f2' : 'white'
                          }}>
                          <input
                            type="radio"
                            name={`collection-change-${prod.ref_frs}`}
                            value="replace"
                            checked={collectionChangeActions[prod.ref_frs] === 'replace'}
                            onChange={() => setCollectionChangeActions({...collectionChangeActions, [prod.ref_frs]: 'replace'})}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm">🔄 Remplacer</p>
                            <p className="text-xs text-gray-600">Supprimer l'ancienne</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Modified Products Detail */}
            {analysis.analysis.modified_count > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">🔄 Produits Modifiés ({analysis.analysis.modified_count})</h3>
                  <button
                    onClick={() => setShowDetailedDiff(!showDetailedDiff)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200"
                  >
                    <Eye size={16} />
                    {showDetailedDiff ? 'Masquer' : 'Voir'} les détails
                  </button>
                </div>
                
                {showDetailedDiff && (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {analysis.modified_products.map((prod, index) => (
                      <div key={index} className="border rounded p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-semibold">{prod.ref_frs}</p>
                            <p className="text-sm text-gray-600">{prod.existing_name} → {prod.new_name}</p>
                          </div>
                          {prod.has_image && (
                            <div className="flex items-center gap-2 text-blue-600">
                              <Shield size={16} />
                              <span className="text-sm">Image protégée</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-sm">
                          <p className="font-medium mb-1">{prod.modification_count} modification(s):</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-700">
                            {prod.modifications.slice(0, 3).map((mod, i) => (
                              <li key={i}>
                                <strong>{mod.field}</strong>: "{mod.old_value || 'vide'}" → "{mod.new_value || 'vide'}"
                              </li>
                            ))}
                            {prod.modification_count > 3 && (
                              <li className="text-gray-500">... et {prod.modification_count - 3} autres</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-3 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                ← Retour
              </button>
              
              <button
                onClick={handleExecuteImport}
                disabled={importing}
                className="px-6 py-3 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {importing ? 'Import en cours...' : `Exécuter l'import (${analysis.analysis.new_count + analysis.analysis.modified_count} produits)`}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Results */}
        {/* Étape 3.5: Validation des images */}
        {step === 3.5 && imageStats && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">Validation des Images</h2>
            
            {/* Stats */}
            <div className="flex gap-4 mb-6">
              <div className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg">
                {imageStats.total} produits
              </div>
              <div className="px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                {imageStats.found} images trouvées
              </div>
              <div className="px-4 py-2 bg-red-100 text-red-800 rounded-lg">
                {imageStats.not_found} manquantes
              </div>
              <div className="px-4 py-2 bg-purple-100 text-purple-800 rounded-lg">
                {Object.keys(validatedImages).length} validées
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              Des images ont été automatiquement détectées pour vos produits. Vous pouvez valider ou ignorer ces associations avant l'import.
            </p>

            {/* Liste des détections (limité aux 10 premiers) */}
            <div className="space-y-2 mb-6 max-h-96 overflow-y-auto">
              {imageDetections.slice(0, 20).map((detection) => (
                <div
                  key={detection.product_id}
                  className={`border rounded p-3 flex items-center gap-3 ${
                    validatedImages[detection.product_id] ? 'bg-green-50 border-green-200' :
                    detection.status === 'found' ? 'bg-white border-gray-200' : 'bg-red-50 border-red-200'
                  }`}
                >
                  {/* Status Icon */}
                  <div>
                    {validatedImages[detection.product_id] ? (
                      <CheckCircle className="text-green-600" size={20} />
                    ) : detection.status === 'found' ? (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded"></div>
                    ) : (
                      <XCircle className="text-red-500" size={20} />
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1">
                    <div className="font-medium">{detection.ref_frs}</div>
                    {detection.status === 'found' && detection.recommended_image && (
                      <div className="text-sm text-gray-600">
                        → {detection.recommended_image.matched_code}{detection.recommended_image.extension}
                      </div>
                    )}
                    {detection.status === 'not_found' && (
                      <div className="text-sm text-red-600">Aucune image trouvée</div>
                    )}
                  </div>

                  {/* Actions */}
                  {detection.status === 'found' && (
                    <div>
                      {!validatedImages[detection.product_id] ? (
                        <button
                          onClick={() => setValidatedImages(prev => ({
                            ...prev,
                            [detection.product_id]: detection.recommended_image
                          }))}
                          className="px-3 py-1 text-sm border border-green-600 text-green-600 rounded hover:bg-green-50"
                        >
                          Valider
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            const newValidated = { ...validatedImages };
                            delete newValidated[detection.product_id];
                            setValidatedImages(newValidated);
                          }}
                          className="px-3 py-1 text-sm border border-red-600 text-red-600 rounded hover:bg-red-50"
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {imageDetections.length > 20 && (
                <div className="text-center text-gray-500 text-sm py-2">
                  ... et {imageDetections.length - 20} autres produits
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={skipImageValidation}
                className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Ignorer cette étape
              </button>
              <button
                onClick={() => {
                  const allValidated = {};
                  imageDetections.forEach(detection => {
                    if (detection.status === 'found' && detection.recommended_image) {
                      allValidated[detection.product_id] = detection.recommended_image;
                    }
                  });
                  setValidatedImages(allValidated);
                }}
                className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Tout valider
              </button>
              <button
                onClick={applyImageAssociations}
                className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Continuer l'import ({Object.keys(validatedImages).length} images validées)
              </button>
            </div>
          </div>
        )}

        {step === 4 && importResult && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center mb-6">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              <h2 className="text-2xl font-bold text-green-700">Import Terminé !</h2>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{importResult.stats.products_imported}</p>
                <p className="text-sm text-gray-600">Produits importés</p>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded p-4 text-center">
                <p className="text-2xl font-bold text-orange-700">{importResult.stats.products_updated}</p>
                <p className="text-sm text-gray-600">Produits mis à jour</p>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded p-4 text-center">
                <p className="text-2xl font-bold text-purple-700">{importResult.stats.products_replaced}</p>
                <p className="text-sm text-gray-600">Produits remplacés</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{importResult.stats.collections_created}</p>
                <p className="text-sm text-gray-600">Collections créées</p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-center">
                <p className="text-2xl font-bold text-yellow-700">{importResult.stats.products_skipped}</p>
                <p className="text-sm text-gray-600">Produits ignorés</p>
              </div>
              
              <div className="bg-cyan-50 border border-cyan-200 rounded p-4 text-center">
                <p className="text-2xl font-bold text-cyan-700">{importResult.stats.images_protected}</p>
                <p className="text-sm text-gray-600">Images protégées</p>
              </div>
            </div>

            {importResult.stats.errors && importResult.stats.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-4 mb-6">
                <h3 className="font-semibold text-red-700 mb-2">⚠️ Erreurs ({importResult.stats.errors.length}):</h3>
                <ul className="list-disc list-inside text-sm text-red-600 max-h-40 overflow-y-auto">
                  {importResult.stats.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={resetImport}
                className="flex-1 px-6 py-3 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Nouvel import
              </button>
              
              <button
                onClick={() => navigate('/admin/products')}
                className="flex-1 px-6 py-3 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Voir les produits
              </button>
            </div>
          </div>
        )}

        {/* Modal de Nettoyage */}
        {showCleanupModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeCleanupModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {/* Header Modal */}
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Trash2 size={24} className="text-red-500" />
                  Nettoyage Produits
                </h2>
                <button onClick={closeCleanupModal} className="p-2 hover:bg-gray-100 rounded">
                  <X size={24} />
                </button>
              </div>

              {/* Étape 1: Sélection */}
              {cleanupStep === 1 && (
                <div className="p-6 space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      ⚠️ Cette action supprimera les produits sélectionnés. Les <strong>images seront préservées</strong> pour réaffectation ultérieure.
                    </p>
                  </div>

                  <div>
                    <label className="block font-medium mb-2">Fournisseur *</label>
                    <select
                      value={selectedSupplierCleanup}
                      onChange={(e) => {
                        setSelectedSupplierCleanup(e.target.value);
                        setSelectedGammeCleanup('');
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded"
                    >
                      <option value="">-- Sélectionnez un fournisseur --</option>
                      {suppliersList.map((supplier, index) => (
                        <option key={index} value={supplier.name}>
                          {supplier.name} ({supplier.product_count} produits)
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedSupplierCleanup && (
                    <div>
                      <label className="block font-medium mb-2">Collection / Gamme (optionnel)</label>
                      <select
                        value={selectedGammeCleanup}
                        onChange={(e) => setSelectedGammeCleanup(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded"
                      >
                        <option value="">-- Toutes les collections --</option>
                        {suppliersList.find(s => s.name === selectedSupplierCleanup)?.gammes.map((gamme, index) => (
                          <option key={index} value={gamme}>{gamme}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={closeCleanupModal}
                      className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={previewCleanup}
                      disabled={!selectedSupplierCleanup}
                      className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
                    >
                      Prévisualiser
                    </button>
                  </div>
                </div>
              )}

              {/* Étape 2: Prévisualisation */}
              {cleanupStep === 2 && cleanupPreview && (
                <div className="p-6 space-y-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="font-bold text-red-800 mb-2">⚠️ Attention !</h3>
                    <p className="text-sm text-red-700">
                      Vous êtes sur le point de supprimer <strong>{cleanupPreview.total_products} produits</strong>.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 border rounded p-4">
                      <p className="text-2xl font-bold text-gray-900">{cleanupPreview.total_products}</p>
                      <p className="text-sm text-gray-600">Produits à supprimer</p>
                    </div>
                    <div className="bg-green-50 border rounded p-4">
                      <p className="text-2xl font-bold text-green-700">{cleanupPreview.products_with_images}</p>
                      <p className="text-sm text-gray-600">Images préservées</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Filtre appliqué :</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700">
                      <li><strong>Fournisseur :</strong> {cleanupPreview.filter.supplier}</li>
                      {cleanupPreview.filter.gamme && (
                        <li><strong>Collection :</strong> {cleanupPreview.filter.gamme}</li>
                      )}
                    </ul>
                  </div>

                  {cleanupPreview.sample.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Exemples de produits :</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {cleanupPreview.sample.map((product, index) => (
                          <div key={index} className="bg-gray-50 border rounded p-3 text-sm">
                            <p><strong>{product.name}</strong></p>
                            <p className="text-gray-600">Ref: {product.ref_frs}</p>
                            {product.has_image && (
                              <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                                <Shield size={12} /> Image préservée
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setCleanupStep(1)}
                      className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Retour
                    </button>
                    <button
                      onClick={executeCleanup}
                      className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Confirmer la suppression
                    </button>
                  </div>
                </div>
              )}

              {/* Étape 3: Résultat */}
              {cleanupStep === 3 && cleanupResult && (
                <div className="p-6 space-y-6">
                  <div className="text-center">
                    <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
                    <h3 className="text-2xl font-bold text-green-700">Nettoyage Terminé !</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-red-50 border border-red-200 rounded p-4 text-center">
                      <p className="text-2xl font-bold text-red-700">{cleanupResult.deleted_count}</p>
                      <p className="text-sm text-gray-600">Produits supprimés</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">{cleanupResult.images_preserved}</p>
                      <p className="text-sm text-gray-600">Images préservées</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📋 Prochaine étape</h4>
                    <p className="text-sm text-blue-800">
                      Vous pouvez maintenant réimporter vos produits avec le fichier Excel mis à jour.
                      Les images seront automatiquement réaffectées si les <strong>ref-frs</strong> correspondent.
                    </p>
                  </div>

                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={closeCleanupModal}
                      className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Fermer
                    </button>
                    <button
                      onClick={() => navigate('/admin/products')}
                      className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      Voir les produits
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminImport;
